import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchSlackUsers, type SlackUser } from "../_components/form-fields/slackUsers";

/** Fetches Slack workspace users for a connector and returns the list, a
 *  Map keyed by user ID, and the underlying query state. The list is shared
 *  across consumers via react-query (5-minute staleTime), so multiple
 *  selectors on the same workspace deduplicate to one network request. */
export function useSlackUsers(connectorId: string | undefined, connectorType: string | undefined, enabled: boolean) {
  const isSlack = !!connectorId && !!connectorType && connectorType.includes("slack");
  const shouldFetch = isSlack && enabled;

  const {
    data: slackUsers = [],
    isLoading,
    isError,
  } = useQuery<SlackUser[]>({
    queryKey: ["slack-users", connectorId, connectorType],
    queryFn: () => fetchSlackUsers(connectorId!, connectorType!),
    enabled: shouldFetch,
    staleTime: 5 * 60 * 1000,
  });

  const slackUsersMap = useMemo(() => new Map(slackUsers.map(user => [user.id, user])), [slackUsers]);

  return {
    slackUsers,
    slackUsersMap,
    isSlackConnector: isSlack,
    // Only surface the loading state when we're actually fetching — otherwise
    // disabled queries report isLoading=true forever.
    isLoading: shouldFetch && isLoading,
    isError,
  };
}
